import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Row, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import ReactApexChart from 'react-apexcharts'
import AdmissionLayout, { useDarkSkin } from './AdmissionLayout'
import { fetchByProvince, fetchSummary } from './api/admissionApi'
import { CLASS_BY_STUDY, CLASS_LABEL } from './fields'
import { useLoading } from '../contexts/LoadingContext'

// ภาพรวมงานรับสมัคร
//
// สีของกราฟ: ชาย/หญิง เป็นหมวดหมู่ (identity) ไม่ใช่ระดับความมาก จึงใช้สองสีคนละเฉด
// ไม่ใช่เฉดอ่อน-เข้มของสีเดียว และเลี่ยงคู่ฟ้า-ชมพูที่เป็นภาพจำเรื่องเพศ
// ทั้งสองคู่ผ่านการตรวจระยะห่างสำหรับตาบอดสี (deutan/protan/tritan) แล้ว
// โหมดมืดใช้เฉดของตัวเองที่เลือกมาให้อยู่ในช่วงความสว่างที่เหมาะกับพื้นเข้ม
// ไม่ใช่สีเดียวกับโหมดสว่าง
const SERIES_COLORS = {
  light: ['#506fd9', '#0cb785'],
  dark:  ['#506fd9', '#0aa87a'],
}
const SINGLE_COLOR = { light: '#506fd9', dark: '#506fd9' }
const SURFACE = { light: '#ffffff', dark: '#1a1a19' }

const CLASS_ORDER = Object.values(CLASS_BY_STUDY).flat().map(([v]) => v)

const StatTile = ({ label, value, hint, icon }) => (
  <Col xs={6} xl={3}>
    <Card className="card-one h-100">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-1">
          <span className="fs-sm text-secondary">{label}</span>
          <i className={`${icon} fs-5 text-secondary`} />
        </div>
        <h3 className="mb-0 fw-bold">{value.toLocaleString('th-TH')}</h3>
        {hint && <div className="fs-xs text-secondary mt-1">{hint}</div>}
      </Card.Body>
    </Card>
  </Col>
)

// เนื้อหาอยู่ในคอมโพเนนต์ลูกเพราะ useDarkSkin อ่านค่าจาก context ที่ AdmissionLayout สร้าง —
// เรียกจากตัว AdmissionDashboard เองจะได้ค่า default เสมอ เพราะ provider อยู่ใน JSX ที่มันคืนออกมา
function DashboardBody({ summary, provinces }) {
  const dark = useDarkSkin()
  const mode = dark ? 'dark' : 'light'

  // นับรวมเป็นตาราง class x gender เรียงตามลำดับชั้นจริง ไม่ใช่ตามตัวอักษร
  const { classes, male, female, totals } = useMemo(() => {
    const present = CLASS_ORDER.filter((c) => summary.some((r) => r.class === c))
    const at = (cls, g) => summary.find((r) => r.class === cls && r.gender === g)?.total || 0
    return {
      classes: present,
      male: present.map((c) => at(c, 'm')),
      female: present.map((c) => at(c, 'f')),
      totals: present.map((c) => at(c, 'm') + at(c, 'f')),
    }
  }, [summary])

  const grandTotal = summary.reduce((sum, r) => sum + Number(r.total), 0)
  const totalIn = (studies) =>
    summary.filter((r) => studies.includes(r.study)).reduce((sum, r) => sum + Number(r.total), 0)
  const totalGender = (g) =>
    summary.filter((r) => r.gender === g).reduce((sum, r) => sum + Number(r.total), 0)

  const stackedOptions = {
    chart: {
      type: 'bar', stacked: true, toolbar: { show: false }, fontFamily: 'inherit',
      background: 'transparent',
    },
    theme: { mode },
    colors: SERIES_COLORS[mode],
    plotOptions: {
      bar: {
        columnWidth: '52%',
        borderRadius: 4,
        borderRadiusApplication: 'end',   // มนเฉพาะปลายบนของกอง ไม่ใช่ทุกท่อน
      },
    },
    // เส้นสีพื้นหลังคั่น 2px ระหว่างท่อนที่ซ้อนกัน ให้แยกออกจากกันโดยไม่ต้องใช้เส้นขอบเข้ม
    stroke: { show: true, width: 2, colors: [SURFACE[mode]] },
    // ป้ายตัวเลขบนแท่ง — เป็นทั้งความสะดวกและตัวช่วยอ่านเมื่อสีเขียวคอนทราสต์ต่ำ
    dataLabels: {
      enabled: true,
      formatter: (val) => (val > 0 ? val : ''),
      style: { fontSize: '11px', fontWeight: 500, colors: ['#fff'] },
    },
    xaxis: {
      categories: classes.map((c) => CLASS_LABEL[c] || c),
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { title: { text: 'จำนวน (คน)' } },
    grid: { borderColor: dark ? '#2c2c2b' : '#e9ebf1', strokeDashArray: 3 },
    legend: { position: 'top', horizontalAlign: 'left', markers: { radius: 3 } },
    tooltip: { shared: true, intersect: false, y: { formatter: (v) => `${v} คน` } },
    noData: { text: 'ยังไม่มีผู้สมัคร' },
  }

  const provinceOptions = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent' },
    theme: { mode },
    colors: [SINGLE_COLOR[mode]],
    plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 4, borderRadiusApplication: 'end' } },
    dataLabels: { enabled: true, style: { fontSize: '11px', colors: ['#fff'] } },
    xaxis: {
      categories: provinces.map((p) => p.province),
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    grid: { borderColor: dark ? '#2c2c2b' : '#e9ebf1', strokeDashArray: 3 },
    // ชุดข้อมูลเดียว ไม่ต้องมีคำอธิบายสี — หัวการ์ดบอกอยู่แล้วว่าคืออะไร
    legend: { show: false },
    tooltip: { y: { formatter: (v) => `${v} คน` } },
    noData: { text: 'ยังไม่มีข้อมูล' },
  }

  return (
    <>
      <Row className="g-3 mb-3">
        <StatTile label="ผู้สมัครทั้งหมด" value={grandTotal} icon="ri-team-line"
          hint="ที่ยังไม่ถูกจัดเข้าห้องเรียน" />
        <StatTile label="มัธยม" value={totalIn(['junior', 'high'])} icon="ri-graduation-cap-line"
          hint="ม.ต้น + ม.ปลาย" />
        <StatTile label="อนุบาล-ประถม" value={totalIn(['anubal', 'prathom'])} icon="ri-bear-smile-line"
          hint="อนุบาล + ประถม" />
        <StatTile label="ชาย / หญิง" value={totalGender('m')} icon="ri-user-line"
          hint={`หญิง ${totalGender('f').toLocaleString('th-TH')} คน`} />
      </Row>

      <Row className="g-3 mb-3">
        <Col xl={8}>
          <Card className="card-one h-100">
            <Card.Header>
              <Card.Title as="h6" className="mb-0">ผู้สมัครแยกตามชั้นและเพศ</Card.Title>
            </Card.Header>
            <Card.Body>
              <ReactApexChart
                key={`stacked-${mode}`}
                type="bar"
                height={320}
                options={stackedOptions}
                series={[
                  { name: 'ชาย', data: male },
                  { name: 'หญิง', data: female },
                ]}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col xl={4}>
          <Card className="card-one h-100">
            <Card.Header>
              <Card.Title as="h6" className="mb-0">ภูมิลำเนาของผู้สมัคร</Card.Title>
            </Card.Header>
            <Card.Body>
              <ReactApexChart
                key={`province-${mode}`}
                type="bar"
                height={320}
                options={provinceOptions}
                series={[{ name: 'ผู้สมัคร', data: provinces.map((p) => Number(p.total)) }]}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ตารางเดียวกับกราฟด้านบน — อ่านค่าที่แน่นอนได้ และใช้แทนกราฟได้เมื่ออ่านสีไม่ได้ */}
      <Card className="card-one">
        <Card.Header>
          <Card.Title as="h6" className="mb-0">ตารางสรุปรายชั้น</Card.Title>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th>ชั้น</th>
                  <th className="text-end">ชาย</th>
                  <th className="text-end">หญิง</th>
                  <th className="text-end">รวม</th>
                </tr>
              </thead>
              <tbody>
                {classes.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-secondary py-4">ยังไม่มีผู้สมัคร</td></tr>
                )}
                {classes.map((cls, i) => (
                  <tr key={cls}>
                    <td>{CLASS_LABEL[cls] || cls}</td>
                    <td className="text-end">{male[i]}</td>
                    <td className="text-end">{female[i]}</td>
                    <td className="text-end fw-semibold">{totals[i]}</td>
                  </tr>
                ))}
              </tbody>
              {classes.length > 0 && (
                <tfoot>
                  <tr className="fw-semibold">
                    <td>รวมทั้งหมด</td>
                    <td className="text-end">{totalGender('m')}</td>
                    <td className="text-end">{totalGender('f')}</td>
                    <td className="text-end">{grandTotal}</td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </Card.Body>
      </Card>
    </>
  )
}

export default function AdmissionDashboard() {
  const { setLoading } = useLoading()
  const [summary, setSummary] = useState([])
  const [provinces, setProvinces] = useState([])

  useEffect(() => {
    setLoading(true)
    Promise.all([fetchSummary(), fetchByProvince()])
      .then(([s, p]) => { setSummary(s); setProvinces(p) })
      .finally(() => setLoading(false))
  }, [setLoading])

  return (
    <AdmissionLayout
      title="ภาพรวมงานรับสมัคร"
      breadcrumb={[{ label: 'ภาพรวม' }]}
      actions={
        <>
          <Button as={Link} to="/admission/applicants" variant="outline-primary">
            <i className="ri-list-check me-1" />รายชื่อผู้สมัคร
          </Button>
          <Button as={Link} to="/admission/register" variant="primary">
            <i className="ri-user-add-line me-1" />รับสมัครใหม่
          </Button>
        </>
      }
    >
      <DashboardBody summary={summary} provinces={provinces} />
    </AdmissionLayout>
  )
}
